import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import HeaderPage from '~/components/HeaderPage';
import { ApplicationState } from '~/store';
import {
  anonymRequest,
  deleteAnonymRequest,
  postAnonymRequest,
} from '~/store/ducks/EliminacaoAnonimizacao/actions';
import { personalRequest } from '~/store/ducks/MostrarInformacoesPessoais/actions';
import getValidationErrors from '../../utils/getValidationErrors';
import { Container, StyledButton } from './styles';

interface ThemeState {
  theme: 'light' | 'dark' | undefined;
}

const EliminacaoAnonimizacao: React.FC = () => {
  const _reCaptchaRef = useRef<ReCAPTCHA>(null);

  const dispatch = useDispatch();

  const personalState = useSelector<ApplicationState, any>(
    state => state.mostrarInformacoesPessoais.data,
  );

  const anonym = useSelector<ApplicationState, any>(
    state => state.EliminacaoAnonimizacao.data,
  );

  useEffect(() => {
    document.title = 'Eliminação / Anonimização dos dados';
    dispatch(personalRequest(0));
  }, [dispatch]);

  const [anonimizar, setAnonimizar] = useState(false);

  useEffect(() => {
    const statusConta: string[] = [];

    if (personalState?.data_status) {
      statusConta.push(personalState?.data_status);
    }

    if (!statusConta.includes('Ativo') && statusConta.length > 0) {
      setAnonimizar(true);
    }
  }, [personalState]);

  const anonymGet = useCallback(() => {
    dispatch(anonymRequest());
  }, [dispatch]);

  useEffect(() => {
    document.title = 'Eliminação / Anonimização dos dados | Portal LGPD';
    anonymGet();
  }, [anonymGet]);

  const theme = useSelector<ApplicationState, ThemeState>(state => state.theme);

  const [isVerified, setIsVerified] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const [recaptchaTheme, setRecaptchaTheme] = useState(true);
  const [formErr, setFormErr] = useState<any>({});

  useEffect(() => {
    setTimeout(() => setRecaptchaTheme(state => !state), 10);
    setTimeout(() => setRecaptchaTheme(state => !state), 20);

    _reCaptchaRef.current?.reset();
  }, [theme.theme]);

  const verifyCallback = (token: string | null) => {
    if (token) {
      setIsVerified(true);
      setRecaptcha(token);
    }
  };

  const expiredCallback = () => {
    setIsVerified(false);
    setRecaptcha('');
  };

  const sendRequest = useCallback(async () => {
    try {
      const data = { isVerified };

      const schema = Yup.object().shape({
        isVerified: Yup.bool().oneOf([true], 'Por favor preencha o reCAPTCHA'),
      });

      await schema.validate(data, { abortEarly: false });

      dispatch(postAnonymRequest(recaptcha));

      _reCaptchaRef.current?.reset();
      setIsVerified(false);
      setRecaptcha('');
    } catch (error) {
      const err = getValidationErrors(error);

      if (!isVerified) {
        toast.error('Por favor, preencha o reCAPTCHA');
      }

      setFormErr(err);

      return;
    }
  }, [dispatch, isVerified, recaptcha]);

  const deleteRequest = useCallback(async () => {
    try {
      const data = { isVerified };

      const schema = Yup.object().shape({
        isVerified: Yup.bool().oneOf([true], 'Por favor preencha o reCAPTCHA'),
      });

      await schema.validate(data, { abortEarly: false });

      dispatch(deleteAnonymRequest(recaptcha));

      _reCaptchaRef.current?.reset();
      setIsVerified(false);
      setRecaptcha('');
    } catch (error) {
      const err = getValidationErrors(error);

      if (!isVerified) {
        toast.error('Por favor, preencha o reCAPTCHA');
      }

      setFormErr(err);

      return;
    }
  }, [dispatch, isVerified, recaptcha]);

  return (
    <Container>
      <HeaderPage pageTitle="Eliminação / Anonimização dos dados" />

      <p style={{ marginTop: 20 }}>
        Caso você não possua contratos ativos com a companhia, poderá ser
        solicitada a eliminação / anonimização dos seus dados. Em contratos
        ativos, a coleta de dados é necessária para a devida prestação dos
        serviços e demais garantias previstas pela Lei Geral de Proteção de
        Dados. Por essa razão, não é possível realizar eliminação /
        anonimização.
      </p>

      {!!anonym.request_anonym_dt ? (
        <>
          <h2 className="cancel-text">
            Sua última solicitação de deleção foi na data de{' '}
            {format(new Date(anonym.request_anonym_dt), 'dd/MM/yyyy')}
          </h2>
        </>
      ) : null}

      <div className="ReCAPTCHA">
        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && recaptchaTheme ? (
          <ReCAPTCHA
            //@ts-ignore
            style={{ display: 'inline-block' }}
            theme={theme.theme}
            ref={_reCaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={verifyCallback}
            onExpired={expiredCallback}
          />
        ) : (
          ''
        )}
      </div>

      <div
        className={`buttons ${
          !!anonym.request_anonym_dt ? 'cancelar' : 'enviar'
        }`}
      >
        <StyledButton
          onClick={() => {
            if (isVerified) {
              if (!!anonym.request_anonym_dt) {
                const toastrConfirmOptions = {
                  onOk: () => deleteRequest(),
                  onCancel: () => {},
                  okText: 'Sim',
                  cancelText: 'Cancelar',
                };

                toastr.confirm(
                  'Tem certeza que deseja solicitar o cancelamento da eliminação / anonimização dos dados?',
                  toastrConfirmOptions,
                );
              } else {
                if (!anonimizar) {
                  const toastrConfirmOptions = {
                    onOk: () => {},
                    onCancel: () => {},
                    okText: 'Ok',
                    disableCancel: true,
                  };

                  toastr.confirm(
                    'Não é possível solicitar a eliminação / anonimização dos dados enquanto houver contratos ativos, conforme legislação vigente. Para maiores informações, entre em contato com o DPO.',
                    toastrConfirmOptions,
                  );
                } else {
                  const toastrConfirmOptions = {
                    onOk: () => sendRequest(),
                    onCancel: () => {},
                    okText: 'Sim',
                    cancelText: 'Cancelar',
                  };

                  toastr.confirm(
                    'Tem certeza que deseja solicitar a eliminação / anonimização?',
                    toastrConfirmOptions,
                  );
                }
              }
            } else {
              toast.error('Por favor, preencha o reCAPTCHA');
            }
          }}
        >
          {!!anonym.request_anonym_dt
            ? 'Cancelar solicitação'
            : 'Enviar solicitação'}
        </StyledButton>
      </div>
    </Container>
  );
};

export default EliminacaoAnonimizacao;
